import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/app-shell';
import { HomeRedirect } from '@/components/layout/home-redirect';
import { ProtectedRoute } from '@/components/layout/protected-route';
import { RequireAgentRoute } from '@/components/layout/require-agent-route';

import LoginPage from '@/pages/login';
import AcceptInvitePage from '@/pages/accept-invite';
import SignupPage from '@/pages/signup';
import ForgotPasswordPage from '@/pages/forgot-password';
import ResetPasswordPage from '@/pages/reset-password';
import VerifyEmailPage from '@/pages/verify-email';
import SelectWorkspacePage from '@/pages/select-workspace';
import ImpersonateHandoffPage from '@/pages/impersonate';
import DashboardPage from '@/pages/dashboard';
import CampaignsPage from '@/pages/campaigns';
import CampaignDetailPage from '@/pages/campaign-detail';
import CampaignWizardPage from '@/pages/campaign-wizard';
import ContactsPage from '@/pages/contacts';
import ContactsImportPage from '@/pages/contacts-import';
import SendersPage from '@/pages/senders';
import MessagesPage from '@/pages/messages';
import InboundPage from '@/pages/inbound';
import ReportsPage from '@/pages/reports';
import BillingPage from '@/pages/billing';
import DevelopersPage from '@/pages/developers';
import WebhooksPage from '@/pages/webhooks';
import HelpPage from '@/pages/help';
import SettingsPage from '@/pages/settings';
import AdminBrandingPage from '@/pages/admin-branding';
import AdminDashboardPage from '@/pages/admin-dashboard';
import AdminOpsPage from '@/pages/admin-ops';
import AdminEarningsPage from '@/pages/admin-earnings';
import AdminCustomersPage from '@/pages/admin-customers';
import AdminCustomerDetailPage from '@/pages/admin-customer-detail';
import AdminSignupsPage from '@/pages/admin-signups';
import AdminPlansPage from '@/pages/admin-plans';
import AdminPlanOverridesPage from '@/pages/admin-plan-overrides';
import AdminRateCardsPage from '@/pages/admin-rate-cards';
import AdminRatesPage from '@/pages/admin-rates';
import AdminRatesAllPage from '@/pages/admin-rates-all';
import AdminProvidersPage from '@/pages/admin-providers';
import AdminWorkersPage from '@/pages/admin-workers';
import AdminAuditPage from '@/pages/admin-audit';
import AdminSendersPage from '@/pages/admin-senders';
import AdminSpamReviewPage from '@/pages/admin-spam-review';
import AdminSpamKeywordsPage from '@/pages/admin-spam-keywords';
import AgentCommissionsPage from '@/pages/agent-commissions';

// Voice module pages — lazy-loaded so the bundle doesn't carry them
// when VOICE_ENABLED is off (typical for SMS-only tenants). The
// VoiceFeatureRoute outlet 404s when useFeature('voice') is false, so
// the chunks are only fetched after the flag flips on.
const VoiceTrunksPage = lazy(() => import('@/pages/voice/trunks'));
const VoiceTrunkDetailPage = lazy(() => import('@/pages/voice/trunk-detail'));
const VoiceDIDsPage = lazy(() => import('@/pages/voice/dids'));
const VoiceCallerIDPoolsPage = lazy(() => import('@/pages/voice/caller-id-pools'));
const VoiceAssetsPage = lazy(() => import('@/pages/voice/assets'));
const VoiceFlowsPage = lazy(() => import('@/pages/voice/flows'));
const VoiceFlowEditorPage = lazy(() => import('@/pages/voice/flow-editor'));
const AIAgentProfilesPage = lazy(() => import('@/pages/voice/ai-agent-profiles'));
const AIAgentProfileEditorPage = lazy(() => import('@/pages/voice/ai-agent-profile-editor'));
const AIAgentCallsPage = lazy(() => import('@/pages/voice/ai-agent-calls'));
const AIAgentInsightsPage = lazy(() => import('@/pages/voice/ai-agent-insights'));
const AIAgentLeadsPage = lazy(() => import('@/pages/voice/leads'));
const BusinessHoursPage = lazy(() => import('@/pages/voice/business-hours'));
const VoicemailsPage = lazy(() => import('@/pages/voice/voicemails'));
const InboundRecordingsPage = lazy(() => import('@/pages/voice/recordings'));
const WebhookSecretPage = lazy(() => import('@/pages/voice/webhook-secret'));
const VoiceLivePage = lazy(() => import('@/pages/voice/live'));
const VoiceCampaignsPage = lazy(() => import('@/pages/voice/campaigns'));
const VoiceCampaignWizardPage = lazy(() => import('@/pages/voice/campaign-wizard'));
const VoiceCampaignQuickWizardPage = lazy(() => import('@/pages/voice/campaign-quick-wizard'));
const VoiceCampaignDetailPage = lazy(() => import('@/pages/voice/campaign-detail'));
const VoiceCampaignReportPage = lazy(() => import('@/pages/voice/campaign-report'));
const VoiceReportsPage = lazy(() => import('@/pages/voice/reports'));
const AdminVoiceRateCardsPage = lazy(() => import('@/pages/voice/admin-rate-cards'));
const AdminVoiceRatesPage = lazy(() => import('@/pages/voice/admin-rates'));
const AdminVoiceCatalogPage = lazy(() => import('@/pages/voice/admin-catalog'));

function VoiceLazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

/**
 * Public route: /login
 * Protected layout: ProtectedRoute → AppShell, with the page components
 *                   rendered inside AppShell's <Outlet />.
 *
 * Anything that isn't matched falls through to a redirect via
 * HomeRedirect, which sends resellers/platform users to /admin/dashboard
 * and customers to /dashboard. Anonymous users hit ProtectedRoute first
 * and bounce to /login.
 */

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    // Public self-service sign-up. POSTs to /v1/auth/signup; the account
    // stays pending until a platform admin approves it.
    path: '/signup',
    element: <SignupPage />,
  },
  {
    // Public — no auth required. The form posts the email to
    // /v1/auth/forgot-password, which always returns 202 regardless
    // of whether the account exists (anti-enumeration).
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    // Public — the :token in the URL is the raw reset token from the
    // email link. The page validates it via /v1/auth/reset-password.
    path: '/reset-password/:token',
    element: <ResetPasswordPage />,
  },
  {
    // Public — the magic-link in the invite email lands here with
    // ?token=XXX. The page POSTs to /v1/auth/accept-invite and
    // auto-signs the user in on success.
    path: '/accept-invite',
    element: <AcceptInvitePage />,
  },
  {
    // Public — fired automatically on page load, hits
    // /v1/auth/verify-email with the token. No form, just status copy.
    path: '/verify-email/:token',
    element: <VerifyEmailPage />,
  },
  {
    // Public route — no auth guard. Reads the access token from the URL
    // fragment, stashes it in sessionStorage, then redirects into the
    // protected app where ProtectedRoute picks it up via getAccessToken().
    path: '/impersonate',
    element: <ImpersonateHandoffPage />,
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      // Multi-tenant picker — rendered outside AppShell because the
      // sidebar/topbar are scoped to the active tid and would imply a
      // workspace has already been picked. Single-membership users
      // never see this; login routes them straight to home.
      { path: 'select-workspace', element: <SelectWorkspacePage /> },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomeRedirect /> },
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'campaigns', element: <CampaignsPage /> },
          { path: 'campaigns/new', element: <CampaignWizardPage /> },
          { path: 'campaigns/:id/edit', element: <CampaignWizardPage /> },
          { path: 'campaigns/:id', element: <CampaignDetailPage /> },
          { path: 'contacts', element: <ContactsPage /> },
          { path: 'contacts/import', element: <ContactsImportPage /> },
          { path: 'senders', element: <SendersPage /> },
          { path: 'messages', element: <MessagesPage /> },
          { path: 'inbound', element: <InboundPage /> },
          { path: 'reports', element: <ReportsPage /> },
          { path: 'billing', element: <BillingPage /> },
          { path: 'developers', element: <DevelopersPage /> },
          { path: 'webhooks', element: <WebhooksPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'help', element: <HelpPage /> },
          { path: 'admin/dashboard', element: <AdminDashboardPage /> },
          // Unified Operations command center — combines the former System
          // Health panel and Live Ops voice monitor into one screen.
          { path: 'admin/ops', element: <AdminOpsPage /> },
          // Legacy split pages, now consolidated into /admin/ops. Redirect old
          // bookmarks/links instead of 404ing. The AdminVoiceOpsPage /
          // AdminHealthPage components stay in the tree (imported nowhere) so
          // the redirect is the single source of truth for the entry point.
          { path: 'admin/voice-ops', element: <Navigate to="/admin/ops" replace /> },
          { path: 'admin/health', element: <Navigate to="/admin/ops" replace /> },
          { path: 'admin/earnings', element: <AdminEarningsPage /> },
          { path: 'admin/customers', element: <AdminCustomersPage /> },
          { path: 'admin/customers/:id', element: <AdminCustomerDetailPage /> },
          { path: 'admin/signups', element: <AdminSignupsPage /> },
          { path: 'admin/plans', element: <AdminPlansPage /> },
          { path: 'admin/plans/:id/overrides', element: <AdminPlanOverridesPage /> },
          { path: 'admin/rate-cards', element: <AdminRateCardsPage /> },
          { path: 'admin/rate-cards/:cardId', element: <AdminRatesPage /> },
          { path: 'admin/rates', element: <AdminRatesAllPage /> },
          { path: 'admin/providers', element: <AdminProvidersPage /> },
          { path: 'admin/workers', element: <AdminWorkersPage /> },
          { path: 'admin/audit', element: <AdminAuditPage /> },
          { path: 'admin/branding', element: <AdminBrandingPage /> },
          { path: 'admin/senders', element: <AdminSendersPage /> },
          { path: 'admin/spam-review', element: <AdminSpamReviewPage /> },
          { path: 'admin/spam-keywords', element: <AdminSpamKeywordsPage /> },
          // Agent portal — guarded by RequireAgentRoute so only
          // role='agent' callers reach the page. Wrong role → bounced
          // back through HomeRedirect to wherever they actually live.
          {
            path: 'agent',
            element: <RequireAgentRoute />,
            children: [
              { path: 'commissions', element: <AgentCommissionsPage /> },
            ],
          },
          // Voice module — routes always declared, but the API endpoints
          // they call return 404 unless VOICE_ENABLED is on AND the
          // tenant has feature_voice=true. Sidebar gating in
          // useFeature('voice') prevents users from finding these URLs
          // organically when the flag is off.
          {
            path: 'voice/trunks',
            element: (
              <VoiceLazy>
                <VoiceTrunksPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/trunks/:id',
            element: (
              <VoiceLazy>
                <VoiceTrunkDetailPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/dids',
            element: (
              <VoiceLazy>
                <VoiceDIDsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/caller-id-pools',
            element: (
              <VoiceLazy>
                <VoiceCallerIDPoolsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/assets',
            element: (
              <VoiceLazy>
                <VoiceAssetsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/flows',
            element: (
              <VoiceLazy>
                <VoiceFlowsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/flows/new',
            element: (
              <VoiceLazy>
                <VoiceFlowEditorPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/flows/:id',
            element: (
              <VoiceLazy>
                <VoiceFlowEditorPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/ai-agents',
            element: (
              <VoiceLazy>
                <AIAgentProfilesPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/ai-agents/calls',
            element: (
              <VoiceLazy>
                <AIAgentCallsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/ai-agents/insights',
            element: (
              <VoiceLazy>
                <AIAgentInsightsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/ai-agents/leads',
            element: (
              <VoiceLazy>
                <AIAgentLeadsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/ai-agents/new',
            element: (
              <VoiceLazy>
                <AIAgentProfileEditorPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/ai-agents/:id',
            element: (
              <VoiceLazy>
                <AIAgentProfileEditorPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/business-hours',
            element: (
              <VoiceLazy>
                <BusinessHoursPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/voicemails',
            element: (
              <VoiceLazy>
                <VoicemailsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/recordings',
            element: (
              <VoiceLazy>
                <InboundRecordingsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/webhook-secret',
            element: (
              <VoiceLazy>
                <WebhookSecretPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/live',
            element: (
              <VoiceLazy>
                <VoiceLivePage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/campaigns',
            element: (
              <VoiceLazy>
                <VoiceCampaignsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/campaigns/new',
            element: (
              <VoiceLazy>
                <VoiceCampaignWizardPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/campaigns/quick',
            element: (
              <VoiceLazy>
                <VoiceCampaignQuickWizardPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/campaigns/:id/edit',
            element: (
              <VoiceLazy>
                <VoiceCampaignWizardPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/campaigns/:id',
            element: (
              <VoiceLazy>
                <VoiceCampaignDetailPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/campaigns/:id/report',
            element: (
              <VoiceLazy>
                <VoiceCampaignReportPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'voice/reports',
            element: (
              <VoiceLazy>
                <VoiceReportsPage />
              </VoiceLazy>
            ),
          },
          {
            // Legacy standalone inbound report — merged into the tabbed
            // Voice Reports page. Redirect so old bookmarks/links land on
            // the Inbound tab instead of 404ing.
            path: 'voice/inbound',
            element: <Navigate to="/voice/reports?tab=inbound" replace />,
          },
          {
            path: 'admin/voice/rate-cards',
            element: (
              <VoiceLazy>
                <AdminVoiceRateCardsPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'admin/voice/rates',
            element: (
              <VoiceLazy>
                <AdminVoiceRatesPage />
              </VoiceLazy>
            ),
          },
          {
            path: 'admin/voice/catalog',
            element: (
              <VoiceLazy>
                <AdminVoiceCatalogPage />
              </VoiceLazy>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <HomeRedirect />,
  },
]);
